import { DestroyRef, Injectable, effect, inject } from '@angular/core';
import { CustomerFeatureFlagsService } from '../../../../core/services/feature-flags.service';
import { User } from '../../../../core/models/user';
import { NonceService } from '../nonce.service';
import { environment } from '../../../../../environments/environment';

@Injectable()
export class SupportWidgetService {
  private readonly featureFlagsService = inject(CustomerFeatureFlagsService);
  private readonly user = inject(User);
  private readonly nonceService = inject(NonceService);
  private readonly destroyRef = inject(DestroyRef);

  private activeWidget: 'zoho' | 'support_bridge' | null = null;

  /**
   * Initialize the widget lifecycle effect.
   * Must be called within an injection context (e.g. component constructor).
   */
  initialize(): void {
    effect(() => {
      const flags = this.featureFlagsService.flags();
      if (!flags) return;

      const newWidget = this.featureFlagsService.hasFlag('support_bridge') ? 'support_bridge' : 'zoho';
      if (this.activeWidget === newWidget) return;

      this.removeSupportWidgets();
      this.activeWidget = newWidget;

      if (newWidget === 'support_bridge') {
        this.addSupportBridgeWidget();
      } else {
        this.addZohoScript();
      }
    });

    this.destroyRef.onDestroy(() => this.removeSupportWidgets());
  }

  private removeSupportWidgets(): void {
    (window as any).SupportBridge?.destroy?.();
    document.querySelector('script[src*="widget/v1/widget.js"]')?.remove();
    document.getElementById('support-bridge-widget')?.remove();
    document.querySelector('script[src*="desk.zoho.com"]')?.remove();
    document.getElementById('zohohc-asap-web-launcherbox')?.remove();
    document.getElementById('zohohc-asap-web-helper')?.remove();
    this.activeWidget = null;
  }

  private addZohoScript(): void {
    const nonce = this.nonceService.generateNonce();
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://desk.zoho.com/portal/api/web/inapp/224546000107746001?orgId=654924228';
    script.defer = true;
    script.nonce = nonce;
    document.body.appendChild(script);
  }

  private addSupportBridgeWidget(): void {
    const script = document.createElement('script');
    script.src = `${(environment as any).supportBridgeUrl}/widget/v1/widget.js`;
    script.defer = true;
    script.onload = () => {
      (window as any).SupportBridge?.init({
        baseUrl: (environment as any).supportBridgeUrl,
        token: this.user.access_token,
        orgId: String(this.user.organization_id),
        orgName: this.user.selected_organization?.name || '',
        userName: `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim(),
        userEmail: this.user.email || '',
      });
    };
    document.body.appendChild(script);
  }
}
